import { supabase } from "../lib/supabase";

const unwrap = ({ data, error }) => {
  if (error) throw error;
  return data;
};

const emptyDashboard = {
  metrics: {},
  vehicles: [],
  drivers: [],
  trips: [],
  requests: [],
  maintenance: [],
  fuel_logs: [],
  safety_events: [],
  sensor_readings: [],
  geofences: [],
  integrations: [],
};

const TRANSPORT_APPROVAL_BUCKET = "transport-approvals";
const MAX_APPROVAL_DOCUMENT_BYTES = 5 * 1024 * 1024;
const APPROVAL_DOCUMENT_TYPES = ["application/pdf", "image/jpeg", "image/png"];

const call = async (functionName, params) =>
  unwrap(await supabase.rpc(functionName, params));

const transportService = {
  async releaseExpiredAssignments() {
    return call("transport_release_expired_assignments");
  },

  async getStaffDashboard() {
    try {
      await transportService.releaseExpiredAssignments();
    } catch (error) {
      // Keep the existing transport workspace usable while an administrator
      // is applying the optional expiry-release migration. Other permission
      // and database errors must still surface normally.
      const message = String(error?.message || "").toLowerCase();
      if (!message.includes("transport_release_expired_assignments")) {
        throw error;
      }
    }
    return (await call("transport_staff_dashboard")) || emptyDashboard;
  },

  async getMyRequests() {
    return (await call("transport_my_requests_with_assignments")) || [];
  },

  async getAvailableDrivers() {
    return (await call("transport_available_drivers")) || [];
  },

  async getApprovalDocumentUrl(filePath, expiresIn = 900) {
    if (!filePath) {
      throw new Error("This request has no viewable approval document.");
    }
    const { data, error } = await supabase.storage
      .from(TRANSPORT_APPROVAL_BUCKET)
      .createSignedUrl(filePath, expiresIn);
    if (error) throw error;
    if (!data?.signedUrl) {
      throw new Error("The approval document could not be opened.");
    }
    return data.signedUrl;
  },

  async createRequest({
    requestType,
    purpose,
    origin,
    destination,
    travelDate,
    returnDate,
    departureTime,
    returnTime,
    passengers,
    vehicleType,
    priority = "normal",
    notes = "",
    outsideCampus = false,
    approvalDocument = null,
  }) {
    let uploadedDocument = null;
    if (approvalDocument) {
      if (
        approvalDocument.size <= 0 ||
        approvalDocument.size > MAX_APPROVAL_DOCUMENT_BYTES ||
        !APPROVAL_DOCUMENT_TYPES.includes(approvalDocument.type)
      ) {
        throw new Error(
          "Approval documents must be a PDF, JPG or PNG up to 5 MB.",
        );
      }
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError || !user?.id) {
        throw (
          userError ||
          new Error(
            "Authentication is required to upload the approval document.",
          )
        );
      }
      const safeName = approvalDocument.name
        .replace(/[^a-zA-Z0-9._-]/g, "_")
        .slice(-120);
      const randomId =
        globalThis.crypto?.randomUUID?.() ||
        `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const storagePath = `${user.id}/${randomId}-${safeName}`;
      const { data, error: uploadError } = await supabase.storage
        .from(TRANSPORT_APPROVAL_BUCKET)
        .upload(storagePath, approvalDocument, {
          contentType: approvalDocument.type,
          upsert: false,
        });
      if (uploadError) throw uploadError;
      uploadedDocument = {
        path: data?.path || storagePath,
        name: approvalDocument.name,
        size: approvalDocument.size,
        mimeType: approvalDocument.type,
      };
    }

    try {
      return await call("transport_create_request", {
        p_request_type: requestType,
        p_purpose: purpose,
        p_origin: origin,
        p_destination: destination,
        p_travel_date: travelDate || null,
        p_departure_time: departureTime || null,
        p_return_time: returnTime || null,
        p_passengers: Number(passengers || 1),
        p_vehicle_type: vehicleType,
        p_priority: priority,
        p_notes: notes || null,
        p_return_date: returnDate || null,
        p_outside_campus: Boolean(outsideCampus),
        p_approval_document_path: uploadedDocument?.path || null,
        p_approval_document_name: uploadedDocument?.name || null,
        p_approval_document_size: uploadedDocument?.size || null,
        p_approval_document_mime_type: uploadedDocument?.mimeType || null,
      });
    } catch (error) {
      if (uploadedDocument?.path) {
        await supabase.storage
          .from(TRANSPORT_APPROVAL_BUCKET)
          .remove([uploadedDocument.path]);
      }
      throw error;
    }
  },

  async updateRequestStatus(requestId, status, staffNote = "") {
    return call("transport_update_request_status", {
      p_request_id: requestId,
      p_status: status,
      p_staff_note: staffNote || null,
    });
  },

  async assignRequest({ requestId, vehicleId, driverProfileId }) {
    return call("transport_assign_request", {
      p_request_id: requestId,
      p_vehicle_id: vehicleId,
      p_driver_profile_id: driverProfileId,
    });
  },

  async createVehicle({
    fleetNumber = null,
    registrationNumber,
    makeModel,
    vehicleType,
    powertrain,
    capacity,
    odometerKm,
    nextServiceDate,
    insuranceExpiry,
    inspectionExpiry,
  }) {
    return call("transport_create_vehicle", {
      p_fleet_number: fleetNumber || null,
      p_registration_number: registrationNumber,
      p_make_model: makeModel,
      p_vehicle_type: vehicleType,
      p_powertrain: powertrain,
      p_capacity: Number(capacity || 1),
      p_odometer_km: Number(odometerKm || 0),
      p_next_service_date: nextServiceDate || null,
      p_insurance_expiry: insuranceExpiry || null,
      p_inspection_expiry: inspectionExpiry || null,
    });
  },

  async updateVehicleStatus(vehicleId, status) {
    return call("transport_update_vehicle_status", {
      p_vehicle_id: vehicleId,
      p_status: status,
    });
  },

  async addTrip({
    requestId,
    vehicleId,
    driverId,
    purpose,
    origin,
    destination,
    departureAt,
    returnAt,
    passengers,
  }) {
    return call("transport_create_trip", {
      p_request_id: requestId || null,
      p_vehicle_id: vehicleId || null,
      p_driver_id: driverId || null,
      p_purpose: purpose,
      p_origin: origin,
      p_destination: destination,
      p_departure_at: departureAt || null,
      p_return_at: returnAt || null,
      p_passengers: Number(passengers || 1),
    });
  },
};

export { transportService };
export default transportService;
